import React, { useMemo } from 'react';
import classnames from 'classnames';
import { createPictureFactory, Picture } from '@funtech-int/yango-shared';
import { useSignal } from '@funtech-int/rival-react';
import { createSignal } from '@funtech-int/rival';

import styles from './_MovieTitle.module.css';

const movieLogo = createPictureFactory({
    fallback: {
        src: '960x540',
    },
});

interface MovieTitleProps {
    logo: string | undefined;
    title: string;
    alt: string;
}

type LoadState = 'IDLE' | 'ERROR' | 'SUCCESS';

export const MovieTitle = ({ logo, title, alt }: MovieTitleProps) => {
    const { img } = movieLogo({ fallbackUrl: logo?.toString() });

    const { onLoad, onError, loadStateSignal } = useMemo(() => {
        const loadState = createSignal<LoadState>(img.src ? 'IDLE' : 'SUCCESS');

        return {
            onLoad: () => loadState.set('SUCCESS'),
            onError: () => loadState.set('ERROR'),
            loadStateSignal: loadState.seal(),
        };
    }, [img.src]);

    const loadState = useSignal(loadStateSignal);

    const logoIsVisible = loadState !== 'ERROR' && img.src;

    const isLongTitle = title.length > 13;

    return (
        <h1 className={classnames(styles.root, { [styles.isVisible]: loadState === 'SUCCESS' })}>
            {logoIsVisible ? (
                <Picture
                    className={styles.logo}
                    alt={alt}
                    aria-label={alt}
                    loading="lazy"
                    srcSet={img.srcSet}
                    src={img.src}
                    data-tid="AdaptiveImage"
                    onLoad={onLoad}
                    onError={onError}
                />
            ) : (
                <span
                    className={classnames(styles.logoPlaceholder, {
                        [styles.isLongTitle]: isLongTitle,
                    })}
                >
                    {title}
                </span>
            )}
            <span className={styles.transparentText}>{title}</span>
        </h1>
    );
};
